import { inject, Injectable } from '@angular/core'
import { HttpParams } from '@angular/common/http'
import { Observable } from 'rxjs'
import { MediaHubApiService } from './media-hub-api.service'
import { ArchiveChatResponse, ChatQueryParams, CreateChatRequest, CreateChatResponse, DeleteChatResponse, GetChatResponse, GetChatsResponse, SendMessageRequest, SendMessageResponse, UpdateChatTitleRequest, UpdateChatTitleResponse } from './asset-chat-api.service'

export interface AddPlaylistsToChatRequest {
  playlistIds: string[]
}

export interface AddPlaylistsToChatResponse {
  success: boolean
  message: string
}

export interface RemovePlaylistsFromChatRequest {
  playlistIds: string[]
}

export interface RemovePlaylistsFromChatResponse {
  success: boolean
  message: string
}




@Injectable({
  providedIn: 'root',
})
export class GeneralChatApiService {
  private readonly endpoint = '/general-chats'
  private apiService = inject(MediaHubApiService)

  createGeneralChat(request?: CreateChatRequest): Promise<CreateChatResponse> {
    return this.apiService.postBase<CreateChatRequest, CreateChatResponse>(this.endpoint, request || {})
  }

  createGeneralChat$(request?: CreateChatRequest): Observable<CreateChatResponse> {
    return this.apiService.postBaseRx<CreateChatRequest, CreateChatResponse>(this.endpoint, request || {})
  }

  sendMessage(chatId: string, request: SendMessageRequest): Promise<SendMessageResponse> {
    return this.apiService.postBase<SendMessageRequest, SendMessageResponse>(`${this.endpoint}/${chatId}/message`, request)
  }

  sendMessage$(chatId: string, request: SendMessageRequest): Observable<SendMessageResponse> {
    return this.apiService.postBaseRx<SendMessageRequest, SendMessageResponse>(`${this.endpoint}/${chatId}/message`, request)
  }

  getGeneralChats(filters?: ChatQueryParams): Promise<GetChatsResponse> {
    const params = this.buildQueryParams(filters)
    return this.apiService.getBase<GetChatsResponse>(this.endpoint, params)
  }

  getGeneralChats$(filters?: ChatQueryParams): Observable<GetChatsResponse> {
    const params = this.buildQueryParams(filters)
    return this.apiService.getBaseRx<GetChatsResponse>(this.endpoint, params)
  }

  getGeneralChatsByProject(projectId: string): Promise<GetChatsResponse> {
    return this.getGeneralChats({ projectId })
  }

  getGeneralChatsByProject$(projectId: string): Observable<GetChatsResponse> {
    return this.getGeneralChats$({ projectId })
  }

  getSingleChat(chatId: string): Promise<GetChatResponse> {
    return this.apiService.getBase<GetChatResponse>(`${this.endpoint}/${chatId}`)
  }

  getSingleChat$(chatId: string): Observable<GetChatResponse> {
    return this.apiService.getBaseRx<GetChatResponse>(`${this.endpoint}/${chatId}`)
  }

  deleteChat$(chatId: string): Observable<DeleteChatResponse> {
    return this.apiService.deleteBaseRx<DeleteChatResponse>(`${this.endpoint}/${chatId}`)
  }

  archiveChat(chatId: string): Promise<ArchiveChatResponse> {
    return this.apiService.postBase<void, ArchiveChatResponse>(`${this.endpoint}/${chatId}/archive`)
  }

  archiveChat$(chatId: string): Observable<ArchiveChatResponse> {
    return this.apiService.postBaseRx<void, ArchiveChatResponse>(`${this.endpoint}/${chatId}/archive`)
  }

  deleteChat(chatId: string): Promise<DeleteChatResponse> {
    return this.apiService.deleteBase<DeleteChatResponse>(`${this.endpoint}/${chatId}`)
  }

  updateChatTitle(chatId: string, request: UpdateChatTitleRequest): Promise<UpdateChatTitleResponse> {
    return this.apiService.putBase<UpdateChatTitleRequest, UpdateChatTitleResponse>(`${this.endpoint}/${chatId}`, request)
  }

  updateChatTitle$(chatId: string, request: UpdateChatTitleRequest): Observable<UpdateChatTitleResponse> {
    return this.apiService.putBaseRx<UpdateChatTitleRequest, UpdateChatTitleResponse>(`${this.endpoint}/${chatId}`, request)
  }

  addPlaylistsToChat(chatId: string, playlistIds: string[]): Promise<AddPlaylistsToChatResponse> {
    const request: AddPlaylistsToChatRequest = { playlistIds }
    return this.apiService.postBase<AddPlaylistsToChatRequest, AddPlaylistsToChatResponse>(`${this.endpoint}/${chatId}/playlists`, request)
  }

  addPlaylistsToChat$(chatId: string, playlistIds: string[]): Observable<AddPlaylistsToChatResponse> {
    const request: AddPlaylistsToChatRequest = { playlistIds }
    return this.apiService.postBaseRx<AddPlaylistsToChatRequest, AddPlaylistsToChatResponse>(`${this.endpoint}/${chatId}/playlists`, request)
  }

  removePlaylistsFromChat(chatId: string, playlistIds: string[]): Promise<RemovePlaylistsFromChatResponse> {
    const request: RemovePlaylistsFromChatRequest = { playlistIds }
    return this.apiService.deleteBaseWithBody<RemovePlaylistsFromChatRequest, RemovePlaylistsFromChatResponse>(`${this.endpoint}/${chatId}/playlists`, request)
  }

  removePlaylistsFromChat$(chatId: string, playlistIds: string[]): Observable<RemovePlaylistsFromChatResponse> {
    const request: RemovePlaylistsFromChatRequest = { playlistIds }
    return this.apiService.deleteBaseWithBodyRx<RemovePlaylistsFromChatRequest, RemovePlaylistsFromChatResponse>(`${this.endpoint}/${chatId}/playlists`, request)
  }

  private buildQueryParams(filters?: ChatQueryParams): HttpParams | undefined {
    if (!filters) return undefined

    let params = new HttpParams()

    if (filters.projectId) {
      params = params.set('projectId', filters.projectId)
    }

    return params
  }
}
